import { useEffect, useRef } from "react";
import { Check, Search } from "lucide-react";

/**
 * Lista de opciones compartida por el sheet móvil y el popover desktop de
 * FormSelect variant="menu". Item: icono opcional + label + description opcional
 * + check del seleccionado. Buscador opcional arriba.
 */
export default function OptionList({
  options,
  value,
  activeIndex,
  onPick,
  onHover,
  accent = "brand",
  showSearch = false,
  query = "",
  onQuery,
  autoFocusSearch = false,
  listboxId,
  onKeyDown,
}) {
  const listRef = useRef(null);

  // Mantener la opción activa (teclado) a la vista
  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${activeIndex}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const selCls =
    accent === "emerald"
      ? "bg-emerald-50 dark:bg-emerald-500/10"
      : "bg-brand/5 dark:bg-blue-500/10";
  const checkCls =
    accent === "emerald"
      ? "text-emerald-600 dark:text-emerald-400"
      : "text-brand dark:text-blue-400";

  return (
    <div>
      {showSearch && (
        <div className="px-1 pb-2">
          <div className="flex items-center gap-2 border border-slate-200/60 dark:border-slate-700/50 rounded-icon bg-transparent dark:bg-transparent px-3 py-2 focus-within:border-brand/50 focus-within:ring-4 focus-within:ring-brand/10 dark:focus-within:border-blue-500/50 dark:focus-within:ring-blue-500/10 transition-all duration-200 ease-out">
            <Search size={15} className="shrink-0 text-slate-400 dark:text-slate-500" />
            <input
              autoFocus={autoFocusSearch}
              value={query}
              onChange={(e) => onQuery?.(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Buscar…"
              className="kv-field-control bg-transparent outline-none w-full text-[16px] text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 min-w-0"
            />
          </div>
        </div>
      )}

      <div role="listbox" id={listboxId} ref={listRef} className="space-y-0.5">
        {options.length === 0 && (
          <p className="px-3 py-3 text-[14px] text-slate-400 dark:text-slate-500">
            Sin resultados
          </p>
        )}
        {options.map((o, i) => {
          const selected = o.value === value;
          const active = i === activeIndex;
          return (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={selected}
              data-idx={i}
              onClick={() => onPick(o.value)}
              onMouseEnter={() => onHover?.(i)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-[12px] text-left transition-colors duration-150 ${
                selected
                  ? selCls
                  : active
                  ? "bg-slate-100 dark:bg-slate-700/50"
                  : "hover:bg-slate-50 dark:hover:bg-slate-800/50"
              }`}
            >
              {o.icon && (
                <span className="shrink-0 text-slate-400 dark:text-slate-500">{o.icon}</span>
              )}
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-medium text-slate-800 dark:text-slate-100 truncate">
                  {o.label}
                </span>
                {o.description && (
                  <span className="block text-[12px] text-slate-400 dark:text-slate-500 truncate">
                    {o.description}
                  </span>
                )}
              </span>
              {selected && <Check size={16} className={`shrink-0 ${checkCls}`} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
